import React, { useState, useEffect } from 'react';
import api from '../config/axios';
import * as XLSX from 'xlsx';

interface Client {
  dni_ruc: string;
  document_type: string;
  name: string;
  legal_representative?: string;
  phone?: string;
  address?: string;
  department: string;
  province: string;
  district: string;
  active: boolean;
  created_at: string;
}

const Clients: React.FC = () => {
  const [clients, setClients] = useState<Client[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  
  // Estados para edición de clientes
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingClient, setEditingClient] = useState<Client | null>(null);
  const [editForm, setEditForm] = useState({
    document_type: 'DNI' as 'DNI' | 'RUC',
    dni_ruc: '',
    name: '',
    legal_representative: '',
    phone: '',
    address: '',
    department: '',
    province: '',
    district: ''
  });
  const [departments, setDepartments] = useState<string[]>([]);
  const [provinces, setProvinces] = useState<string[]>([]);
  const [districts, setDistricts] = useState<string[]>([]);

  useEffect(() => {
    fetchClients();
    fetchDepartments();
  }, []);

  const fetchDepartments = async () => {
    try {
      const response = await api.get('/locations/departments');
      setDepartments(response.data || []);
    } catch (error) {
      console.error('Error al cargar departamentos:', error);
    }
  };

  const fetchClients = async () => {
    try {
      setLoading(true);
      const response = await api.get('/clients');
      setClients(response.data || []);
    } catch (error) {
      console.error('Error al cargar clientes:', error);
      alert('Error al cargar la lista de clientes');
    } finally {
      setLoading(false);
    }
  };

  // Filtrar clientes por término de búsqueda
  const filteredClients = clients.filter(client =>
    client.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    client.dni_ruc.includes(searchTerm) ||
    client.department.toLowerCase().includes(searchTerm.toLowerCase()) ||
    client.province.toLowerCase().includes(searchTerm.toLowerCase()) ||
    client.district.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Paginación
  const totalPages = Math.ceil(filteredClients.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentClients = filteredClients.slice(startIndex, endIndex);

  // Funciones para edición de clientes
  const openEditModal = (client: Client) => {
    setEditingClient(client);
    setEditForm({
      document_type: client.document_type as 'DNI' | 'RUC',
      dni_ruc: client.dni_ruc,
      name: client.name,
      legal_representative: client.legal_representative || '',
      phone: client.phone || '',
      address: client.address || '',
      department: client.department,
      province: client.province,
      district: client.district
    });
    
    // Cargar provincias y distritos
    if (client.department) {
      fetchProvinces(client.department);
      if (client.province) {
        fetchDistricts(client.department, client.province);
      }
    }
    
    setShowEditModal(true);
  };

  const fetchProvinces = async (department: string) => {
    try {
      const response = await api.get(`/locations/provinces/${encodeURIComponent(department)}`);
      setProvinces(response.data || []);
    } catch (error) {
      console.error('Error al cargar provincias:', error);
      setProvinces([]);
    }
  };

  const fetchDistricts = async (department: string, province: string) => {
    try {
      const response = await api.get(`/locations/districts/${encodeURIComponent(department)}/${encodeURIComponent(province)}`);
      setDistricts(response.data || []);
    } catch (error) {
      console.error('Error al cargar distritos:', error);
      setDistricts([]);
    }
  };

  const handleDepartmentChange = (department: string) => {
    setEditForm({...editForm, department, province: '', district: ''});
    setProvinces([]);
    setDistricts([]);
    if (department) {
      fetchProvinces(department);
    }
  };

  const handleProvinceChange = (province: string) => {
    setEditForm({...editForm, province, district: ''});
    setDistricts([]);
    if (province && editForm.department) {
      fetchDistricts(editForm.department, province);
    }
  };

  const saveClientEdit = async () => {
    if (!editingClient) return;

    try {
      const response = await api.put(`/clients/${editingClient.dni_ruc}`, editForm);
      if (response.data) {
        alert('Cliente actualizado exitosamente');
        setShowEditModal(false);
        setEditingClient(null);
        fetchClients(); // Recargar la lista
      }
    } catch (error) {
      console.error('Error al actualizar cliente:', error);
      alert('Error al actualizar el cliente');
    }
  };

  const exportToExcel = () => {
    const worksheetData = [
      ['DNI/RUC', 'Tipo Doc', 'Nombre/Razón Social', 'Representante Legal', 'Teléfono', 'Dirección', 'Departamento', 'Provincia', 'Distrito', 'Estado', 'Fecha Registro']
    ];

    filteredClients.forEach(client => {
      worksheetData.push([
        client.dni_ruc,
        client.document_type,
        client.name,
        client.legal_representative || '',
        client.phone || '',
        client.address || '',
        client.department,
        client.province,
        client.district,
        client.active ? 'Activo' : 'Inactivo',
        new Date(client.created_at).toLocaleDateString()
      ]);
    });

    const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Clientes');
    XLSX.writeFile(workbook, `clientes_${new Date().toISOString().split('T')[0]}.xlsx`);
  };

  if (loading) {
    return (
      <div className="container-fluid">
        <div className="d-flex justify-content-center align-items-center" style={{ height: '400px' }}>
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Cargando...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid">
      <div className="row">
        <div className="col-12">
          <div className="card">
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h5 className="card-title mb-0">Clientes Registrados</h5>
                <button className="btn btn-success btn-sm" onClick={exportToExcel}>
                  📊 Exportar XLSX
                </button>
              </div>

              {/* Barra de búsqueda */}
              <div className="row g-2 mb-3">
                <div className="col-md-6">
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Buscar por nombre, DNI/RUC, departamento, provincia o distrito..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </div>
                <div className="col-md-6">
                  <small className="text-muted">
                    Mostrando {startIndex + 1}-{Math.min(endIndex, filteredClients.length)} de {filteredClients.length} clientes
                  </small>
                </div>
              </div>

              {/* Tabla de clientes */}
              <div className="table-responsive">
                <table className="table table-sm table-hover">
                  <thead>
                    <tr>
                      <th>DNI/RUC</th>
                      <th>Tipo</th>
                      <th>Nombre/Razón Social</th>
                      <th>Representante Legal</th>
                      <th>Teléfono</th>
                      <th>Dirección</th>
                      <th>Ubicación</th>
                      <th>Estado</th>
                      <th>Fecha Registro</th>
                      <th>Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {currentClients.map((client) => (
                      <tr key={client.dni_ruc}>
                        <td>
                          <div className="fw-bold">{client.dni_ruc}</div>
                        </td>
                        <td>
                          <span className="badge bg-info">{client.document_type}</span>
                        </td>
                        <td>
                          <div className="fw-bold">{client.name}</div>
                        </td>
                        <td>
                          <div>{client.legal_representative || '-'}</div>
                        </td>
                        <td>
                          <div>{client.phone || '-'}</div>
                        </td>
                        <td>
                          <div className="text-truncate" style={{ maxWidth: '200px' }} title={client.address}>
                            {client.address || '-'}
                          </div>
                        </td>
                        <td>
                          <div className="text-muted">
                            <small>{client.district}, {client.province}</small><br />
                            <small>{client.department}</small>
                          </div>
                        </td>
                        <td>
                          <span className={`badge ${client.active ? 'bg-success' : 'bg-secondary'}`}>
                            {client.active ? 'Activo' : 'Inactivo'}
                          </span>
                        </td>
                        <td>
                          <div className="text-muted">
                            <small>{new Date(client.created_at).toLocaleDateString()}</small>
                          </div>
                        </td>
                        <td>
                          <button 
                            className="btn btn-sm btn-outline-primary"
                            onClick={() => openEditModal(client)}
                            title="Editar cliente"
                          >
                            ✏️
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Controles de paginación */}
              {totalPages > 1 && (
                <nav aria-label="Paginación de clientes" className="mt-3">
                  <ul className="pagination pagination-sm justify-content-center">
                    <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                      <button 
                        className="page-link" 
                        onClick={() => setCurrentPage(1)}
                        disabled={currentPage === 1}
                      >
                        Primera
                      </button>
                    </li>
                    <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                      <button 
                        className="page-link" 
                        onClick={() => setCurrentPage(currentPage - 1)}
                        disabled={currentPage === 1}
                      >
                        Anterior
                      </button>
                    </li>
                    
                    {/* Mostrar páginas */}
                    {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                      let pageNum: number;
                      if (totalPages <= 5) {
                        pageNum = i + 1;
                      } else if (currentPage <= 3) {
                        pageNum = i + 1;
                      } else if (currentPage >= totalPages - 2) {
                        pageNum = totalPages - 4 + i;
                      } else {
                        pageNum = currentPage - 2 + i;
                      }
                      
                      return (
                        <li key={pageNum} className={`page-item ${currentPage === pageNum ? 'active' : ''}`}>
                          <button 
                            className="page-link" 
                            onClick={() => setCurrentPage(pageNum)}
                          >
                            {pageNum}
                          </button>
                        </li>
                      );
                    })}
                    
                    <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                      <button 
                        className="page-link" 
                        onClick={() => setCurrentPage(currentPage + 1)}
                        disabled={currentPage === totalPages}
                      >
                        Siguiente
                      </button>
                    </li>
                    <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                      <button 
                        className="page-link" 
                        onClick={() => setCurrentPage(totalPages)}
                        disabled={currentPage === totalPages}
                      >
                        Última
                      </button>
                    </li>
                  </ul>
                </nav>
              )}

              {filteredClients.length === 0 && (
                <div className="text-center py-4">
                  <p className="text-muted">No se encontraron clientes que coincidan con la búsqueda.</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Modal de edición de cliente */}
      {showEditModal && (
        <div className="modal show d-block" style={{backgroundColor: 'rgba(0,0,0,0.5)'}}>
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Editar Cliente</h5>
                <button type="button" className="btn-close" onClick={() => setShowEditModal(false)}></button>
              </div>
              <div className="modal-body">
                <div className="row g-3">
                  <div className="col-md-4">
                    <label className="form-label">Tipo de Documento <span className="text-danger">*</span></label>
                    <select 
                      className="form-select" 
                      value={editForm.document_type} 
                      onChange={(e) => setEditForm({...editForm, document_type: e.target.value as 'DNI' | 'RUC', dni_ruc: ''})}
                    >
                      <option value="DNI">DNI</option>
                      <option value="RUC">RUC</option>
                    </select>
                  </div>
                  <div className="col-md-8">
                    <label className="form-label">{editForm.document_type} <span className="text-danger">*</span></label>
                    <input 
                      className="form-control" 
                      value={editForm.dni_ruc} 
                      onChange={(e) => {
                        const value = e.target.value.replace(/\D/g, '');
                        const maxLength = editForm.document_type === 'DNI' ? 8 : 11;
                        if (value.length <= maxLength) {
                          setEditForm({...editForm, dni_ruc: value});
                        }
                      }} 
                      placeholder={`Ingrese ${editForm.document_type === 'DNI' ? '8' : '11'} dígitos`}
                      maxLength={editForm.document_type === 'DNI' ? 8 : 11}
                    />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label">Nombre/Razón Social <span className="text-danger">*</span></label>
                    <input 
                      className="form-control" 
                      value={editForm.name} 
                      onChange={(e) => setEditForm({...editForm, name: e.target.value})} 
                    />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label">Representante Legal</label>
                    <input 
                      className="form-control" 
                      value={editForm.legal_representative} 
                      onChange={(e) => setEditForm({...editForm, legal_representative: e.target.value})} 
                    />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label">Teléfono</label>
                    <input 
                      className="form-control" 
                      value={editForm.phone} 
                      onChange={(e) => setEditForm({...editForm, phone: e.target.value})} 
                    />
                  </div>
                  <div className="col-12">
                    <label className="form-label">Dirección</label>
                    <input 
                      className="form-control" 
                      value={editForm.address} 
                      onChange={(e) => setEditForm({...editForm, address: e.target.value})} 
                    />
                  </div>
                  <div className="col-md-4">
                    <label className="form-label">Departamento <span className="text-danger">*</span></label>
                    <select 
                      className="form-select" 
                      value={editForm.department} 
                      onChange={(e) => handleDepartmentChange(e.target.value)}
                    >
                      <option value="">Seleccione...</option>
                      {departments.map(dept => (
                        <option key={dept} value={dept}>{dept}</option>
                      ))}
                    </select>
                  </div>
                  <div className="col-md-4">
                    <label className="form-label">Provincia <span className="text-danger">*</span></label>
                    <select 
                      className="form-select" 
                      value={editForm.province} 
                      onChange={(e) => handleProvinceChange(e.target.value)}
                      disabled={!editForm.department}
                    >
                      <option value="">Seleccione...</option>
                      {provinces.map(prov => (
                        <option key={prov} value={prov}>{prov}</option>
                      ))}
                    </select>
                  </div>
                  <div className="col-md-4">
                    <label className="form-label">Distrito <span className="text-danger">*</span></label>
                    <select 
                      className="form-select" 
                      value={editForm.district} 
                      onChange={(e) => setEditForm({...editForm, district: e.target.value})}
                      disabled={!editForm.province}
                    >
                      <option value="">Seleccione...</option>
                      {districts.map(dist => (
                        <option key={dist} value={dist}>{dist}</option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowEditModal(false)}>
                  Cancelar
                </button>
                <button type="button" className="btn btn-primary" onClick={saveClientEdit}>
                  Guardar Cambios
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Clients;
